import {
  UNDATED_PATRIOT_BACKFILL_LANES,
  type PatriotBackfillLane,
  type StartPatriotBackfillRequest,
} from "@visioni/a3s-monitoring/app";
import type { DayRangeValues } from "./components/ActivationDateRangeField";

/** What each lane copies, in the words support would use for it. */
export const PATRIOT_BACKFILL_LANE_LABELS: Record<PatriotBackfillLane, string> = {
  signals: "Signals",
  activations: "Activations",
  operatorNotes: "Operator notes",
  clients: "Clients",
  actionPlans: "Action plans",
  clientGroupingTypes: "Grouping types",
  clientGroupings: "Groupings",
};

export interface BackfillRangeFields {
  readonly days: DayRangeValues;
  /** `HH:mm` on the first day; blank is midnight. */
  readonly fromTime: string;
  /** `HH:mm` on the last day; blank is the end of that day. */
  readonly toTime: string;
}

export interface BackfillRangeResult {
  /** Absent until both days are chosen and every field reads. */
  readonly range?: { start: string; end: string };
  readonly errors: { fromTime?: string; toTime?: string; range?: string };
}

/** Whether every lane copies all its records, so the range plays no part. */
export const isUndatedOnly = (lanes: readonly PatriotBackfillLane[]): boolean =>
  lanes.length > 0 && lanes.every((lane) => UNDATED_PATRIOT_BACKFILL_LANES.includes(lane));

export interface BackfillRequestFields extends BackfillRangeFields {
  readonly lanes: readonly PatriotBackfillLane[];
  readonly force: boolean;
}

/**
 * The backfill to start for the form, once it has a lane and, if any lane is
 * dated, a range. Undated lanes ignore the range, so with only those chosen
 * the days and times play no part.
 */
export function backfillRequest({ lanes, force, ...rangeFields }: BackfillRequestFields): {
  readonly request?: StartPatriotBackfillRequest;
  readonly errors: BackfillRangeResult["errors"];
} {
  const [firstLane, ...otherLanes] = lanes;
  if (firstLane === undefined) return { errors: {} };
  const named: StartPatriotBackfillRequest = { lanes: [firstLane, ...otherLanes], force };
  if (isUndatedOnly(lanes)) return { request: named, errors: {} };

  const { range, errors } = backfillRange(rangeFields);
  return range === undefined ? { errors } : { request: { ...named, ...range }, errors };
}

const TIME_ERROR = "Use a 24-hour time, like 18:00";

/**
 * The backfill range for the form's days and times, in the browser's
 * timezone. With no times it covers both days whole; a time narrows its own
 * end, down to a window of minutes within one day.
 */
export function backfillRange(fields: BackfillRangeFields): BackfillRangeResult {
  const fromTime = parseTime(fields.fromTime);
  const toTime = parseTime(fields.toTime);
  const errors = {
    ...(fromTime === "invalid" && { fromTime: TIME_ERROR }),
    ...(toTime === "invalid" && { toTime: TIME_ERROR }),
  };
  if (fromTime === "invalid" || toTime === "invalid") return { errors };

  const start = localTime(fields.days.from, fromTime ?? { hours: 0, minutes: 0 });
  // A blank end time is midnight after the last day, so that day counts whole.
  const end = localTime(fields.days.to, toTime ?? { hours: 24, minutes: 0 });
  if (start === undefined || end === undefined) return { errors };
  if (start >= end) return { errors: { range: "The range has to end after it starts" } };
  return { range: { start: start.toISOString(), end: end.toISOString() }, errors };
}

/** `undefined` for a blank field, `"invalid"` for one that isn't a 24-hour `H:mm`/`HH:mm`. */
function parseTime(text: string): { hours: number; minutes: number } | "invalid" | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return undefined;
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  const hours = Number(match?.[1]);
  const minutes = Number(match?.[2]);
  if (!match || hours > 23 || minutes > 59) return "invalid";
  return { hours, minutes };
}

function localTime(day: string | undefined, time: { hours: number; minutes: number }): Date | undefined {
  if (day === undefined) return undefined;
  const [y, m, d] = day.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return new Date(y, m - 1, d, time.hours, time.minutes);
}
